import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Grid, TextField, Typography, Button, Alert,
    CircularProgress, Divider, Paper, Tooltip, InputAdornment, MenuItem,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import TimerIcon from "@mui/icons-material/Timer";
import LoyaltyIcon from "@mui/icons-material/Loyalty";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const calendarDaysFromToday = (amount, unit) => {
    const n = Math.max(1, Math.floor(Number(amount)));
    if (unit === "days") return n;

    const today = new Date();
    const future = new Date(today);
    if (unit === "years") {
        future.setFullYear(future.getFullYear() + n);
    } else if (unit === "months") {
        future.setMonth(future.getMonth() + n);
    }
    return Math.round((future - today) / (1000 * 60 * 60 * 24));
};

const formatDate = (date) =>
    date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

const sanitizeDaysValue = (value) => {
    if (value == null) return "";
    const raw = String(value).trim();
    if (raw === "") return "";
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return "";
    const floored = Math.floor(numeric);
    if (floored < 0) return "";
    return String(floored);
};

// ── Helper ────────────────────────────────────────────────────────────────────
function SettingField({
    label, helperText, value, onChange, disabled, tooltip,
}) {
    const [amount, setAmount] = useState("");
    const [unit, setUnit] = useState("days");

    useEffect(() => {
        if (value !== "") {
            const currentComputed = amount ? String(calendarDaysFromToday(amount, unit)) : "";
            if (currentComputed !== String(value)) {
                setAmount(String(value));
                setUnit("days");
            }
        } else if (amount !== "") {
            setAmount("");
        }
    }, [value]);

    const triggerChange = (newAmount, newUnit) => {
        if (!newAmount) {
            onChange({ target: { value: "" } });
        } else {
            onChange({ target: { value: String(calendarDaysFromToday(newAmount, newUnit)) } });
        }
    };

    const handleAmountChange = (e) => {
        const newAmount = sanitizeDaysValue(e.target.value);
        setAmount(newAmount);
        triggerChange(newAmount, unit);
    };

    const handleUnitChange = (e) => {
        const newUnit = e.target.value;
        setUnit(newUnit);
        triggerChange(amount, newUnit);
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={0.5} mb={1.5}>
                <Typography fontSize={13} fontWeight={600} color="#1a1a2e">
                    {label}
                </Typography>
                {tooltip && (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "#94a3b8", cursor: "help" }} />
                    </Tooltip>
                )}
            </Box>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Quantité"
                        placeholder="Illimité (laisser vide)"
                        value={amount}
                        disabled={disabled}
                        onChange={handleAmountChange}
                        InputProps={{ inputProps: { min: 0 } }}
                        sx={inputSx}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        select
                        size="small"
                        label="Unité"
                        value={unit}
                        disabled={disabled}
                        onChange={handleUnitChange}
                        sx={inputSx}
                    >
                        <MenuItem value="days">Jours</MenuItem>
                        <MenuItem value="months">Mois</MenuItem>
                        <MenuItem value="years">Années</MenuItem>
                    </TextField>
                </Grid>
            </Grid>
            <Typography fontSize={11} color="text.secondary" mt={0.5}>{helperText}</Typography>
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LoyaltySettingsPanel({ brandName, onNotify }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // form state — empty string means "illimité" (null in backend)
    const [form, setForm] = useState({
        points_validity_days: "",
        loyalty_status_validity_days: "",
    });
    const [original, setOriginal] = useState(null);

    // ── Load current settings ─────────────────────────────────────────────────
    const loadSettings = useCallback(async () => {
        if (!brandName) return;
        setLoading(true);
        setError(null);
        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-settings`, {
                headers: { "X-Brand": brandName },
            });
            if (!res.ok) throw new Error(`Erreur HTTP ${res.status}`);
            const data = await res.json();
            const next = {
                points_validity_days: sanitizeDaysValue(data.points_validity_days),
                loyalty_status_validity_days: sanitizeDaysValue(data.loyalty_status_validity_days),
            };
            setForm(next);
            setOriginal(next);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [brandName]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const isDirty =
        original !== null &&
        (form.points_validity_days !== original.points_validity_days ||
            form.loyalty_status_validity_days !== original.loyalty_status_validity_days);

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: sanitizeDaysValue(e.target.value) }));

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const safePointsValidity = sanitizeDaysValue(form.points_validity_days);
            const safeLoyaltyStatusValidity = sanitizeDaysValue(form.loyalty_status_validity_days);
            const body = {
                points_validity_days: safePointsValidity !== "" ? Number(safePointsValidity) : null,
                loyalty_status_validity_days: safeLoyaltyStatusValidity !== "" ? Number(safeLoyaltyStatusValidity) : null,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-settings`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName,
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            const data = await res.json();
            const next = {
                points_validity_days: sanitizeDaysValue(data.points_validity_days),
                loyalty_status_validity_days: sanitizeDaysValue(data.loyalty_status_validity_days),
                coupon_validity_days: sanitizeDaysValue(data.coupon_validity_days),
            };
            setForm(next);
            setOriginal(next);
            onNotify?.("Paramètres de fidélité mis à jour", "success");
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    if (!brandName) {
        return (
            <Box py={4} textAlign="center">
                <Typography color="text.secondary" fontSize={13}>
                    Sélectionnez une marque pour accéder aux paramètres.
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box py={5} display="flex" justifyContent="center">
                <CircularProgress size={28} sx={{ color: SECTION_COLORS.rewards.main }} />
            </Box>
        );
    }

    return (
        <Box width="100%">

            {/* ── Section: Validité des Points ─────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #f8fbff 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                    },
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 2,
                            background: SECTION_COLORS.rewards.gradient,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 4px 12px ${SECTION_COLORS.rewards.main}30`,
                        }}
                    >
                        <LoyaltyIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Validité des Points
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Durée au-delà de laquelle les points expirent automatiquement
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <SettingField
                            label="Expiration des points"
                            helperText="Laisser vide = points n'expirent jamais (illimité)."
                            tooltip="Durée au-delà de laquelle un point devient invalide."
                            value={form.points_validity_days}
                            onChange={set("points_validity_days")}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box
                            sx={{
                                height: "100%", minHeight: 80, borderRadius: 2,
                                bgcolor: sanitizeDaysValue(form.points_validity_days) !== "" ? "#f0fdf4" : "#f8fafc",
                                border: `1px solid ${sanitizeDaysValue(form.points_validity_days) !== "" ? "#86efac" : "#e2e8f0"}`,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", p: 1.5, gap: 0.5,
                            }}
                        >
                            {sanitizeDaysValue(form.points_validity_days) !== "" ? (
                                <>
                                    <Typography fontSize={22} fontWeight={800} color="#16a34a" fontFamily="Montserrat, sans-serif">
                                        {sanitizeDaysValue(form.points_validity_days)}j
                                    </Typography>
                                    <Typography fontSize={10} color="#16a34a" fontWeight={600}>CONFIGURÉ</Typography>
                                </>
                            ) : (
                                <>
                                    <AllInclusiveIcon sx={{ color: "#94a3b8", fontSize: 28 }} />
                                    <Typography fontSize={10} color="#94a3b8" fontWeight={600}>ILLIMITÉ</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Section: Validité du Statut Fidélité ────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3, p: 3, borderRadius: 3,
                    border: "1px solid #e8ecf1",
                    background: "linear-gradient(135deg, #f8fbff 0%, #fff 100%)",
                    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                    },
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Box
                        sx={{
                            width: 36, height: 36, borderRadius: 2,
                            background: SECTION_COLORS.tiers.gradient,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: `0 4px 12px ${SECTION_COLORS.tiers.main}30`,
                        }}
                    >
                        <LoyaltyIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                    <Box>
                        <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e">
                            Validité du Statut de Fidélité
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">
                            Durée de conservation du niveau (tier) d'un client sans activité
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <SettingField
                            label="Durée de validité du statut"
                            helperText="Laisser vide = le statut ne rétrograde jamais automatiquement."
                            tooltip="Durée d'inactivité au-delà de laquelle le statut fidélité peut être rétrogradé."
                            value={form.loyalty_status_validity_days}
                            onChange={set("loyalty_status_validity_days")}
                            disabled={saving}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box
                            sx={{
                                height: "100%", minHeight: 80, borderRadius: 2,
                                bgcolor: sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? "#eff6ff" : "#f8fafc",
                                border: `1px solid ${sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? "#93c5fd" : "#e2e8f0"}`,
                                display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", p: 1.5, gap: 0.5,
                            }}
                        >
                            {sanitizeDaysValue(form.loyalty_status_validity_days) !== "" ? (
                                <>
                                    <Typography fontSize={22} fontWeight={800} color="#2563eb" fontFamily="Montserrat, sans-serif">
                                        {sanitizeDaysValue(form.loyalty_status_validity_days)}j
                                    </Typography>
                                    <Typography fontSize={10} color="#2563eb" fontWeight={600}>CONFIGURÉ</Typography>
                                </>
                            ) : (
                                <>
                                    <AllInclusiveIcon sx={{ color: "#94a3b8", fontSize: 28 }} />
                                    <Typography fontSize={10} color="#94a3b8" fontWeight={600}>ILLIMITÉ</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* ── Alerts ───────────────────────────────────────────────────── */}
            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* ── Save Button ───────────────────────────────────────────────── */}
            <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button
                    variant="outlined"
                    onClick={loadSettings}
                    disabled={saving || loading}
                    sx={{
                        textTransform: "none", fontWeight: 600, borderRadius: 2,
                        fontFamily: "Montserrat, sans-serif",
                        borderColor: "#e2e8f0", color: "#64748b",
                        "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
                    }}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    sx={{
                        textTransform: "none", fontWeight: 700, borderRadius: 2,
                        fontFamily: "Montserrat, sans-serif",
                        background: isDirty ? SECTION_COLORS.rewards.gradient : undefined,
                        boxShadow: isDirty ? `0 4px 12px ${SECTION_COLORS.rewards.main}30` : undefined,
                        "&:hover": { boxShadow: `0 6px 20px ${SECTION_COLORS.rewards.main}40` },
                        px: 3,
                    }}
                >
                    {saving ? "Enregistrement..." : "Enregistrer"}
                </Button>
            </Box>
        </Box>
    );
}
